import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Replayer } from "rrweb";
import "rrweb/dist/style.css";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Monitor, Users, XCircle, MousePointerClick, ChevronLeft } from "lucide-react";

const WS_URL = (path) => {
  const backend = (typeof window !== "undefined" && window?.__envBackend) || (process.env.REACT_APP_BACKEND_URL || "");
  const ws = backend.replace(/^http/, "ws");
  return `${ws}${path}`;
};

/**
 * SA-only: pošlje mirror zahtevo LD uporabniku, čaka na sprejem,
 * potem prikaže živi DOM mirror (rrweb) prek WebSocket. Deluje tudi za mobilne uporabnike.
 */
const SuperAdminScreenMirror = () => {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const headers = { Authorization: `Bearer ${token}` };

  const [ldFilter, setLdFilter] = useState("");
  const [members, setMembers] = useState([]);
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState("idle"); // idle|waiting|streaming|error|ended
  const [controlling, setControlling] = useState(false);
  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const replayerRef = useRef(null);
  const bufferRef = useRef([]);
  const metaRef = useRef(null);
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const pingRef = useRef(null);
  const chunksRef = useRef({});
  const manualEndRef = useRef(false);
  const retryRef = useRef(0);
  const sessionIdRef = useRef(null);
  const watchRef = useRef(null);

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${API}/sa/screen-mirror/targets`, { headers });
      const flat = [];
      (res.data || []).forEach(ld => {
        (ld.leaders || []).forEach(l => flat.push({ ...l, ld_naziv: ld.naziv, ld_id: ld.id }));
      });
      setMembers(flat);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { fetchMembers(); /* eslint-disable-next-line */ }, []);

  const filtered = members.filter(m => {
    const s = `${m.ime || ""} ${m.priimek || ""} ${m.ld_naziv || ""}`.toLowerCase();
    return s.includes(ldFilter.toLowerCase());
  });

  const request = async (member) => {
    try {
      const res = await axios.post(`${API}/sa/screen-mirror/request`, { ld_user_id: member.id }, { headers });
      setSession({ ...res.data, member });
      setStatus("waiting");
      toast.info(`Zahteva poslana. Čakam na sprejem ${member.ime} ${member.priimek}...`);
      pollRef.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/sa/screen-mirror/sessions`, { headers });
          const cur = (s.data.sessions || []).find(x => x.id === res.data.session_id);
          if (cur?.status === "accepted" || cur?.status === "connecting" || cur?.status === "connected") {
            clearInterval(pollRef.current);
            connectWS(res.data.session_id);
          } else if (cur?.status === "rejected") {
            clearInterval(pollRef.current);
            setStatus("error");
            toast.error("Uporabnik je zavrnil.");
          }
        } catch (e) { /* silent */ }
      }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka");
    }
  };

  const fitScale = () => {
    const el = containerRef.current;
    const meta = metaRef.current;
    if (!el || !meta?.data?.width) return;
    const wrapper = el.querySelector(".replayer-wrapper");
    if (!wrapper) return;
    const scale = Math.min(el.clientWidth / meta.data.width, el.clientHeight / meta.data.height);
    wrapper.style.transform = `scale(${scale})`;
    wrapper.style.transformOrigin = "top left";
    wrapper.style.position = "absolute";
    wrapper.style.left = `${Math.max(0, (el.clientWidth - meta.data.width * scale) / 2)}px`;
    wrapper.style.top = `${Math.max(0, (el.clientHeight - meta.data.height * scale) / 2)}px`;
  };

  const sendControl = (payload) => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "control", ...payload }));
  };

  const eventToFrac = (e) => {
    const wrapper = containerRef.current?.querySelector(".replayer-wrapper");
    if (!wrapper) return null;
    const rect = wrapper.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const fracX = (e.clientX - rect.left) / rect.width;
    const fracY = (e.clientY - rect.top) / rect.height;
    if (fracX < 0 || fracX > 1 || fracY < 0 || fracY > 1) return null;
    return { fracX, fracY };
  };

  const onOverlayClick = (e) => {
    try { e.currentTarget.focus({ preventScroll: true }); } catch { /* ignore */ }
    const f = eventToFrac(e);
    if (f) sendControl({ kind: "click", ...f });
  };

  const toggleControl = () => {
    const ws = wsRef.current;
    const next = !controlling;
    setControlling(next);
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: next ? "control_start" : "control_stop" }));
    }
    toast.info(next ? "Nadzor prevzet – klikaj, tipkaj in drsaj po zaslonu" : "Nadzor sproščen");
    if (next) {
      setTimeout(() => {
        try { containerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* ignore */ }
      }, 100);
    }
  };

  // Wheel (scroll) nadzor – non-passive listener
  useEffect(() => {
    const el = overlayRef.current;
    if (!el || !controlling) return;
    const onWheel = (e) => {
      e.preventDefault();
      const f = eventToFrac(e) || { fracX: 0.5, fracY: 0.5 };
      sendControl({ kind: "scroll", dy: e.deltaY, dx: e.deltaX, ...f });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlling]);

  // Tipkanje na daljavo – med nadzorom pošlji tipke LD odjemalcu
  useEffect(() => {
    if (!controlling) return;
    const onKey = (e) => {
      const a = document.activeElement;
      if (a && (a.tagName === "INPUT" || a.tagName === "TEXTAREA" || a.isContentEditable)) return;
      const special = ["Backspace", "Delete", "Enter", "Tab", "Escape", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End"];
      if (e.key.length !== 1 && !special.includes(e.key)) return;
      e.preventDefault();
      e.stopPropagation();
      sendControl({ kind: "key", key: e.key, ctrl: e.ctrlKey, shift: e.shiftKey, alt: e.altKey, meta: e.metaKey });
    };
    window.addEventListener("keydown", onKey, true);

    // rrweb Replayer replaya Focus event in ukrade fokus v replay iframe –
    // zato poslušamo keydown tudi v iframe-u in fokus vračamo na overlay.
    const hooked = [];
    const hookIframe = () => {
      const ifr = containerRef.current?.querySelector("iframe");
      if (ifr?.contentWindow && !hooked.some(h => h.win === ifr.contentWindow)) {
        try {
          ifr.contentWindow.addEventListener("keydown", onKey, true);
          hooked.push({ win: ifr.contentWindow });
        } catch { /* cross-origin ignore */ }
      }
      const ae = document.activeElement;
      if (ae?.tagName === "IFRAME" && containerRef.current?.contains(ae)) {
        try { overlayRef.current?.focus({ preventScroll: true }); } catch { /* ignore */ }
      }
    };
    hookIframe();
    const iv = setInterval(hookIframe, 400);
    try { overlayRef.current?.focus({ preventScroll: true }); } catch { /* ignore */ }
    return () => {
      window.removeEventListener("keydown", onKey, true);
      clearInterval(iv);
      hooked.forEach(({ win }) => { try { win.removeEventListener("keydown", onKey, true); } catch { /* ignore */ } });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlling]);

  const handleRrwebEvent = (event) => {
    if (event.type === 4) {
      metaRef.current = event;
      requestAnimationFrame(fitScale);
    }
    if (replayerRef.current) {
      try { replayerRef.current.addEvent(event); } catch { /* ignore */ }
      return;
    }
    bufferRef.current.push(event);
    const meta = bufferRef.current.find(x => x.type === 4);
    const snap = bufferRef.current.find(x => x.type === 2);
    if (meta && snap && containerRef.current) {
      containerRef.current.innerHTML = "";
      const rep = new Replayer([], {
        root: containerRef.current,
        liveMode: true,
        mouseTail: false,
        pauseAnimation: false,
        insertStyleRules: [
          ".animate-fade-in{opacity:1!important;transform:none!important}",
          "*{animation-play-state:running!important}",
        ],
        UNSAFE_replayCanvas: false,
      });
      replayerRef.current = rep;
      rep.startLive(bufferRef.current[0].timestamp - 500);
      bufferRef.current.forEach(x => { try { rep.addEvent(x); } catch { /* ignore */ } });
      bufferRef.current = [];
      setStatus("streaming");
      if (watchRef.current) { clearTimeout(watchRef.current); watchRef.current = null; }
      setTimeout(fitScale, 200);
    }
  };

  const connectWS = (sessionId) => {
    sessionIdRef.current = sessionId;
    manualEndRef.current = false;
    const ws = new WebSocket(WS_URL(`/api/sa/screen-mirror/ws/${sessionId}?role=sa&token=${token}`));
    wsRef.current = ws;

    ws.onopen = () => {
      retryRef.current = 0;
      if (pingRef.current) clearInterval(pingRef.current);
      pingRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
      }, 25000);
      if (watchRef.current) clearTimeout(watchRef.current);
      watchRef.current = setTimeout(() => {
        if (!replayerRef.current && !manualEndRef.current) {
          toast.error("Povezava ni uspela – poskusi znova.");
          setStatus("timeout");
        }
      }, 12000);
    };

    ws.onmessage = (ev) => {
      const msg = JSON.parse(ev.data);
      if (msg.type === "rrweb" && msg.data) {
        handleRrwebEvent(msg.data);
      } else if (msg.type === "rrweb_chunk") {
        const acc = chunksRef.current[msg.id] || (chunksRef.current[msg.id] = { parts: [], got: 0 });
        if (acc.parts[msg.seq] === undefined) {
          acc.parts[msg.seq] = msg.part;
          acc.got += 1;
        }
        if (acc.got === msg.total) {
          delete chunksRef.current[msg.id];
          try { handleRrwebEvent(JSON.parse(acc.parts.join(""))); } catch { /* ignore */ }
        }
      } else if (msg.type === "bye") {
        setStatus("ended");
        manualEndRef.current = true;
      }
      // peer_left: LD se bo poskusil ponovno povezati – pustimo odprto
    };

    ws.onclose = () => {
      if (pingRef.current) clearInterval(pingRef.current);
      if (manualEndRef.current) return;
      if (retryRef.current < 15) {
        retryRef.current += 1;
        setTimeout(() => {
          if (!manualEndRef.current && sessionIdRef.current) {
            cleanupReplayer();
            connectWS(sessionIdRef.current);
          }
        }, 3000);
      } else {
        setStatus("ended");
      }
    };
  };

  const cleanupReplayer = () => {
    try { replayerRef.current?.destroy?.(); } catch { /* ignore */ }
    replayerRef.current = null;
    bufferRef.current = [];
    metaRef.current = null;
    chunksRef.current = {};
    if (containerRef.current) containerRef.current.innerHTML = "";
  };

  const endSession = () => {
    manualEndRef.current = true;
    sessionIdRef.current = null;
    setControlling(false);
    try {
      if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify({ type: "bye" }));
      wsRef.current?.close();
    } catch { /* ignore */ }
    if (pollRef.current) clearInterval(pollRef.current);
    if (pingRef.current) clearInterval(pingRef.current);
    if (watchRef.current) { clearTimeout(watchRef.current); watchRef.current = null; }
    chunksRef.current = {};
    cleanupReplayer();
    setSession(null); setStatus("idle");
  };

  useEffect(() => { return () => { endSession(); }; /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const onResize = () => fitScale();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div className="space-y-6" data-testid="sa-screen-mirror">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Monitor className="h-6 w-6" />
          Skupna raba zaslona (Live view)
        </h2>
        <p className="text-sm text-muted-foreground">
          Živi pogled aplikacije LD uporabnika (read-only). Deluje tudi na telefonih (iOS/Android).
        </p>
      </div>

      {status === "idle" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" />Izberi uporabnika (predsednika/gospodarja LD)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              value={ldFilter}
              onChange={e => setLdFilter(e.target.value)}
              placeholder="Iskanje po imenu ali LD ..."
              data-testid="mirror-search"
            />
            <div className="max-h-96 overflow-y-auto border rounded">
              {filtered.slice(0, 100).map(m => (
                <div key={`${m.ld_id}-${m.id}`} className="flex items-center justify-between p-2 border-b hover:bg-muted/40">
                  <div>
                    <div className="font-medium">{m.ime} {m.priimek}</div>
                    <div className="text-xs text-muted-foreground">{m.ld_naziv} · {m.vloga}</div>
                  </div>
                  <Button size="sm" onClick={() => request(m)} data-testid={`mirror-request-${m.id}`}>
                    Zaprosi
                  </Button>
                </div>
              ))}
              {filtered.length === 0 && <div className="p-4 text-center text-sm text-muted-foreground">Ni ustreznih uporabnikov</div>}
            </div>
          </CardContent>
        </Card>
      )}

      {(status === "waiting" || status === "streaming" || status === "ended" || status === "error" || status === "timeout") && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{session?.member?.ime} {session?.member?.priimek}</CardTitle>
                <CardDescription>
                  {status === "waiting" && <Badge>čakam na sprejem ...</Badge>}
                  {status === "streaming" && <Badge className="bg-green-100 text-green-800">poteka v živo</Badge>}
                  {status === "ended" && <Badge variant="secondary">končano</Badge>}
                  {status === "error" && <Badge variant="destructive">zavrnjeno</Badge>}
                  {status === "timeout" && <Badge variant="destructive">povezava ni uspela</Badge>}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {status === "streaming" && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => sendControl({ kind: "back" })}
                      disabled={!controlling}
                      title="Nazaj"
                      data-testid="mirror-back"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={controlling ? "default" : "secondary"}
                      size="sm"
                      onClick={toggleControl}
                      data-testid="mirror-control-toggle"
                      className={controlling ? "bg-amber-600 hover:bg-amber-700" : ""}
                    >
                      <MousePointerClick className="h-4 w-4 mr-1" />
                      {controlling ? "Sprosti nadzor" : "Prevzmi nadzor"}
                    </Button>
                  </>
                )}
                <Button variant="destructive" size="sm" onClick={endSession} data-testid="mirror-end">
                  <XCircle className="h-4 w-4 mr-1" />Prekini
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="relative w-full h-[calc(100vh-240px)] min-h-[340px] bg-slate-900 rounded overflow-hidden">
              <div ref={containerRef} className="absolute inset-0" data-testid="mirror-view" />
              {status === "streaming" && controlling && (
                <div
                  ref={overlayRef}
                  tabIndex={0}
                  className="absolute inset-0 z-20 cursor-pointer outline-none"
                  onClick={onOverlayClick}
                  data-testid="mirror-control-overlay"
                />
              )}
              {status === "streaming" && controlling && (
                <div className="absolute top-2 left-2 z-30 bg-amber-600 text-white text-xs px-2 py-1 rounded shadow pointer-events-none">
                  Nadzor aktiven · klikni, tipkaj ali drsaj
                </div>
              )}
              {status !== "streaming" && (
                <div className="absolute inset-0 flex items-center justify-center text-white/60">
                  {status === "waiting" ? "Čakam na sprejem ..." : status === "error" ? "Uporabnik je zavrnil" : status === "timeout" ? "Povezava ni uspela – poskusi znova" : "Prenos končan"}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SuperAdminScreenMirror;
